import { ArrowPathIcon, ExclamationTriangleIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import useStockPermissions, { STOCK_PRICE_LOCKED_MESSAGE } from "../../data/useStockPermissions";
import { IPart } from "../../models/Part";
import { IListingPriceSummary } from "../../models/listingOptimisation/IListingInsights";
import { IPartIntelligenceReview } from "../../models/listingOptimisation/IListingReview";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import { daysSince } from "../../utils/listingAge";
import { STOCK_HEAVY_THRESHOLD } from "../../utils/listingNextStep";
import PriceFormat from "../../utils/priceFormat";
import { baselinePrice, isPriceLocked } from "../../utils/stockPriceLock";
import { IQueueRowSignals } from "./queueRowSignals";
import { DONE_SECTION_CLASSES } from "./drawerSectionState";
import DoneChip from "./DoneChip";

interface IFocusDrawerPriceSectionProps {
  part: IPart;
  signals: IQueueRowSignals;
  draftPrice: string;
  onDraftPriceChange: (value: string) => void;
  stagedPrice: number | null;
  review: IPartIntelligenceReview | undefined;
  onRefreshLookup: () => void;
  refreshBusy: boolean;
  onSeeListings: () => void;
  /** A new price has been entered, or the live price already sits within 10% of the market: the section turns green. */
  done: boolean;
  /** The entered price is only in the drawer so far: the green section carries a "not saved" tag. */
  unsaved: boolean;
}

const money = (value: number | null | undefined): string => (value != null ? PriceFormat(value) : "—");

const round2 = (value: number): string => (Math.round(value * 100) / 100).toFixed(2);

function SummaryTile({ summary, tone, caption }: { summary: IListingPriceSummary | null | undefined; tone: "sold" | "live"; caption: string }) {
  const classes = tone === "sold" ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-slate-50 text-slate-800";
  const captionClasses = tone === "sold" ? "text-emerald-700" : "text-slate-500";
  return (
    <div className={classNames("flex flex-col gap-0.5 rounded-md border px-2.5 py-2", classes)}>
      <span className="flex justify-between text-[11px] tabular-nums">
        <span>{money(summary?.min)}</span>
        <strong className="text-[13px]">{money(summary?.average)}</strong>
        <span>{money(summary?.max)}</span>
      </span>
      <span className={classNames("text-[10px]", captionClasses)}>{caption}</span>
    </div>
  );
}

/** One sentence on where the live price sits, for the drawer's price section. */
export function priceSummarySentence(signals: IQueueRowSignals, part: IPart): string | null {
  const { livePrice, soldSummary, stockByVehicle } = signals;
  if (livePrice == null) return null;

  const bits: string[] = [];
  const intelligence = part.intelligenceAverage;
  if (intelligence != null && intelligence > 0) {
    const gap = Math.round(((livePrice - intelligence) / intelligence) * 100);
    bits.push(`${Math.abs(gap)}% ${gap >= 0 ? "above" : "below"} the intelligent price`);
  }
  if (soldSummary?.average != null && soldSummary.average > 0) {
    const gap = Math.round(((livePrice - soldSummary.average) / soldSummary.average) * 100);
    bits.push(`${Math.abs(gap)}% ${gap >= 0 ? "above" : "below"} sold`);
  }

  const sentences: string[] = [];
  if (bits.length > 0) sentences.push(`Live price is ${bits.join(" and ")}.`);
  if (stockByVehicle >= STOCK_HEAVY_THRESHOLD) {
    sentences.push(`${stockByVehicle} of the same part off the same car are in stock.`);
  }
  return sentences.length > 0 ? sentences.join(" ") : null;
}

/**
 * The drawer's Price section: the intelligent pricing block (eBay asking prices, AI-reviewed),
 * then what the yard itself has sold, has live and has in stock by vehicle and by part number,
 * and the quick ways to set a new price. The price is only staged - nothing changes on eBay.
 */
export default function FocusDrawerPriceSection({
  part,
  signals,
  draftPrice,
  onDraftPriceChange,
  stagedPrice,
  review,
  onRefreshLookup,
  refreshBusy,
  onSeeListings,
  done,
  unsaved,
}: IFocusDrawerPriceSectionProps) {
  const insights = part.marketInsights ?? null;
  const intelligence = part.intelligenceAverage;
  const lookupDays = daysSince(part.lastIntelligenceLookup);
  const foundCount = review?.foundCount ?? part.intelligenceFoundCount ?? null;
  const keptCount = review?.keptCount ?? part.intelligenceKeptCount ?? null;
  const dropped = foundCount != null && keptCount != null ? foundCount - keptCount : null;
  const soldWindow = insights?.soldByVehicle?.windowDays ?? insights?.soldByPartNumber?.windowDays ?? 90;
  const live = signals.livePrice;
  const sentence = priceSummarySentence(signals, part);

  // Stock:AdminOnlyChanges - staging a new price for a priced listing is admin-only, matching the API.
  const { restricted } = useStockPermissions();
  const priceLocked = isPriceLocked(restricted, baselinePrice(live, part.enteredPrice));

  const quick = (label: string, value: number | null | undefined, extra = "") =>
    value != null && value > 0 ? (
      <button
        type="button"
        onClick={() => onDraftPriceChange(round2(value))}
        className={classNames(
          "inline-flex h-8 items-center rounded-md border border-gray-300 bg-white px-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-50",
          extra
        )}
      >
        {label} · {PriceFormat(value)}
      </button>
    ) : null;

  return (
    <section aria-label="Price" className={classNames("space-y-3 border-b px-6 py-4", done ? DONE_SECTION_CLASSES : "border-gray-200")}>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-[13px] font-semibold text-gray-900">
          Price
          {done ? <DoneChip unsaved={unsaved} /> : null}
        </h3>
        <span className="text-xs text-gray-500">
          Live on eBay <strong className="ml-1 text-base text-gray-900">{money(live)}</strong>
          {stagedPrice != null && (
            <span className="ml-2 rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-800">saved {PriceFormat(stagedPrice)}</span>
          )}
        </span>
      </div>

      <div aria-label="Intelligent pricing" className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-900">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide">
            <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Intelligent pricing · eBay active listings
          </span>
          <span className="text-[11px] text-amber-800">
            {part.lastIntelligenceLookup
              ? `Looked up ${formatDateTime(part.lastIntelligenceLookup, DateTime.DATE_MED)}${lookupDays != null ? ` · ${lookupDays}d ago` : ""}`
              : "Not looked up yet"}
          </span>
        </div>

        {intelligence != null ? (
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="text-[11px]">
              Min <strong className="text-[13px]">{money(part.intelligenceMin ?? intelligence)}</strong>
            </span>
            <span className="text-[11px]">
              Avg <strong className="text-lg">{PriceFormat(intelligence)}</strong>
            </span>
            <span className="text-[11px]">
              Max <strong className="text-[13px]">{money(part.intelligenceMax ?? intelligence)}</strong>
            </span>
            {foundCount != null && keptCount != null && (
              <span className="ml-auto text-[11px] text-amber-800">
                {keptCount} kept of {foundCount} found
                {dropped != null && dropped > 0 ? ` · AI dropped ${dropped}` : ""}
              </span>
            )}
          </div>
        ) : (
          <p className="text-xs">No intelligent price for this part yet. Run a lookup.</p>
        )}

        {review?.searchTerm && <p className="text-[11px] text-amber-800">Searched: “{review.searchTerm}”</p>}

        <div className="flex flex-wrap items-center gap-1.5">
          {!priceLocked && intelligence != null && intelligence > 0 && (
            <button
              type="button"
              onClick={() => onDraftPriceChange(round2(intelligence))}
              className="inline-flex h-8 items-center rounded-md bg-amber-200 px-2.5 text-xs font-semibold text-amber-900 hover:bg-amber-300"
            >
              Use intelligent price · {PriceFormat(intelligence)}
            </button>
          )}
          <button
            type="button"
            onClick={onRefreshLookup}
            disabled={refreshBusy}
            title="Look up eBay again; the AI keeps only the listings that are this part"
            className="inline-flex h-8 items-center gap-1 rounded-md border border-amber-300 bg-white px-2.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-60"
          >
            {refreshBusy ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
            Refresh lookup
          </button>
          <button
            type="button"
            onClick={onSeeListings}
            className="ml-auto h-8 px-2 text-xs font-medium text-amber-900 hover:underline"
          >
            See the {foundCount ?? "listings"} →
          </button>
        </div>
      </div>

      <div className="grid grid-cols-[4rem_minmax(0,1fr)_minmax(0,1fr)] items-stretch gap-1.5">
        <span />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Same car &amp; part</span>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Same MPN / OE</span>

        <span className="self-center text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Sold</span>
        <SummaryTile
          summary={insights?.soldByVehicle}
          tone="sold"
          caption={insights?.soldByVehicle?.count ? `${insights.soldByVehicle.count} sold in ${soldWindow}d` : `none sold in ${soldWindow}d`}
        />
        <SummaryTile
          summary={insights?.soldByPartNumber}
          tone="sold"
          caption={insights?.soldByPartNumber?.count ? `${insights.soldByPartNumber.count} sold in ${soldWindow}d` : `none sold in ${soldWindow}d`}
        />

        <span className="self-center text-[11px] font-semibold uppercase tracking-wide text-slate-600">Our live</span>
        <SummaryTile
          summary={insights?.activeByVehicle}
          tone="live"
          caption={`${insights?.activeByVehicle?.count ?? 0} listed by you`}
        />
        <SummaryTile
          summary={insights?.activeByPartNumber}
          tone="live"
          caption={`${insights?.activeByPartNumber?.count ?? 0} listed by you`}
        />

        <span className="self-center text-[11px] font-semibold uppercase tracking-wide text-sky-700">Stock</span>
        <div className="rounded-md border border-sky-200 bg-sky-50 px-2.5 py-2 text-xs text-sky-900">
          {insights?.stockByVehicle ?? 0} in stock
          {(insights?.stockByVehicle ?? 0) >= STOCK_HEAVY_THRESHOLD && <span className="ml-1 rounded bg-sky-200 px-1 text-[10px] font-bold uppercase">heavy</span>}
        </div>
        <div className="rounded-md border border-sky-200 bg-sky-50 px-2.5 py-2 text-xs text-sky-900">
          {insights?.stockByPartNumber ?? 0} in stock · {insights?.activeByPartNumber?.count ?? 0} listed
        </div>
      </div>

      {priceLocked ? (
        <p className="text-xs text-gray-500">{STOCK_PRICE_LOCKED_MESSAGE}</p>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          {quick("Match sold avg", signals.soldSummary?.average)}
          {live != null && live > 0 && quick("−10%", live * 0.9)}
          <label className="inline-flex h-8 items-center overflow-hidden rounded-md border border-gray-300 bg-white text-xs">
            <span className="px-2 text-gray-500">£</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={draftPrice}
              onChange={(e) => onDraftPriceChange(e.target.value)}
              placeholder="New price"
              className="h-full w-28 border-0 px-1 text-sm tabular-nums focus:ring-0"
              aria-label="New price"
            />
          </label>
          {draftPrice && (
            <button type="button" onClick={() => onDraftPriceChange("")} className="text-xs text-gray-500 hover:text-gray-700">
              Clear
            </button>
          )}
        </div>
      )}

      {sentence && (
        <p className="flex items-start gap-1.5 text-xs text-gray-600">
          <ExclamationTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
          <span>{sentence}</span>
        </p>
      )}
    </section>
  );
}
